import React, { useEffect, useState } from 'react';
import { DateRangePicker } from 'materialui-daterange-picker';
import { Popover, TextField } from '@mui/material';
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  differenceInDays,
} from 'date-fns';
import InputAdornment from '@mui/material/InputAdornment';

const CustomDateRangePicker = ({
  handleDateChange,
  dateValue,
  dateFormat = 'dd MMM yy',
  customPresets = null,
  maxDateRange = null, // Maximum days allowed between start and end date
  onDateRangeError = null, // Callback when date range exceeds max
  errorBorder = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [initialDateRange] = useState(
    dateValue || { startDate: null, endDate: null }
  );
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [resetKey, setResetKey] = useState(0);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleChange = (range) => {
    // Check if max date range is specified and validate
    if (maxDateRange && range.startDate && range.endDate) {
      const daysDiff = differenceInDays(range.endDate, range.startDate);
      if (daysDiff > maxDateRange) {
        if (onDateRangeError) {
          onDateRangeError(`Date range cannot exceed ${maxDateRange} days`);
        }
        return; // Don't apply the range
      }
    }
    setDateRange(range);
    handleDateChange(range);
    handleClose();
  };

  const handleReset = () => {
    setDateRange(initialDateRange);
    handleDateChange(initialDateRange);
    setResetKey(resetKey + 1); // Force a re-render to refresh the DateRangePicker component
  };

  const defaultPresets = [
    { label: 'Today', range: { startDate: new Date(), endDate: new Date() } },
    {
      label: 'Yesterday',
      range: {
        startDate: subDays(new Date(), 1),
        endDate: subDays(new Date(), 1),
      },
    },
    {
      label: 'Last Week',
      range: { startDate: subDays(new Date(), 7), endDate: new Date() },
    },
    {
      label: 'Last Month',
      range: {
        startDate: startOfMonth(subDays(new Date(), 30)),
        endDate: endOfMonth(subDays(new Date(), 30)),
      },
    },
    {
      label: 'Last Quarter',
      range: { startDate: subDays(new Date(), 90), endDate: new Date() },
    },
  ];

  // Use custom presets if provided, otherwise use default
  const presets = customPresets || defaultPresets;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '16px', fontWeight: '500' }} className="text">
          Date Range:{' '}
        </span>
        <TextField
          label=""
          error={errorBorder}
          value={
            dateRange.startDate && dateRange.endDate
              ? `${format(dateRange.startDate, dateFormat)} - ${format(
                  dateRange.endDate,
                  dateFormat
                )}`
              : ''
          }
          onClick={handleClick}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <img
                  src={process.env.PUBLIC_URL + '/assests/img/calendarIcon.svg'}
                  alt=""
                  style={{ height: '20px', cursor: 'pointer' }}
                ></img>
              </InputAdornment>
            ),
          }}
        />
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div id="customDateRangePicker" style={{ display: 'flex' }}>
          {/* Left Side Presets */}
          <div style={{ padding: '10px', minWidth: '120px', zIndex: 1 }}>
            {presets.map((preset) => (
              <div
                key={preset.label}
                style={{
                  cursor: 'pointer',
                  margin: '16px',
                  color: '#000000ff',
                }}
                onClick={() => {
                  handleChange(preset.range);
                }}
              >
                {preset.label}
              </div>
            ))}
            <div
              style={{
                cursor: 'pointer',
                color: 'red',
                color: '#ff0000ff',
                margin: '130px 16px 16px',
              }}
              onClick={handleReset}
            >
              Reset
            </div>
          </div>

          {/* Right Side Calendar */}
          <DateRangePicker
            key={resetKey} // This ensures the DateRangePicker is reset when the key changes
            open={open}
            toggle={() => setOpen(!open)}
            onChange={handleChange}
            initialDateRange={dateRange}
          />
        </div>
      </Popover>
    </div>
  );
};

export default CustomDateRangePicker;
